/**
 * Bloc entreprise. Recherche SIRET animée → champs qui se posent un à un.
 */
import { useState } from "react";
import { CheckField } from "../../components/CheckField";
import { Field } from "../../components/Field";
import { facturxFetch } from "../../api/client";
import { PcgFields } from "../../pcg/PcgFields";
import { PCG_CUSTOMERS } from "../../pcg/pcg";
import { applyClientKind, clientKindOf, type ClientKind } from "../../legal/clientKind";
import { SiretSearch, type SiretPhase } from "./SiretSearch";
import type { ClientDraft, SireneStatus } from "./types";

const KINDS: { id: ClientKind; label: string; hint: string }[] = [
  { id: "particulier", label: "Particulier", hint: "Nom + adresse" },
  { id: "micro", label: "Micro", hint: "SIRET · pas de TVA" },
  { id: "societe", label: "Société", hint: "SIRET, SIREN, TVA" },
];

type Match = Partial<ClientDraft> & {
  sirene_status?: SireneStatus;
  sirene_closed_on?: string | null;
  sirene_created_on?: string | null;
};

type CompanySectionProps = {
  draft: ClientDraft;
  setDraft: (next: ClientDraft) => void;
  onError: (msg: string) => void;
  busy: boolean;
  setBusy: (v: boolean) => void;
  tried?: boolean;
};

export function CompanySection({ draft, setDraft, onError, busy, setBusy, tried }: CompanySectionProps) {
  const [phase, setPhase] = useState<SiretPhase>("idle");
  const [foundName, setFoundName] = useState("");
  const [lookupErr, setLookupErr] = useState("");
  const [flash, setFlash] = useState(0);

  async function lookup() {
    const q = draft.siret.trim() || draft.name.trim();
    if (!q) {
      setPhase("err");
      setLookupErr("Indique un SIRET ou un nom");
      return;
    }
    setBusy(true);
    setPhase("scan");
    setLookupErr("");
    onError("");
    try {
      const data = await facturxFetch<{ match: Match }>(
        `siret-lookup.php?q=${encodeURIComponent(q)}`
      );
      const m = data.match;
      const micro = draft.tax_exempt;
      const next: ClientDraft = {
        ...draft,
        name: m.name || draft.name,
        siret: String(m.siret || draft.siret),
        siren: String(m.siren || draft.siren),
        vat_number: micro ? "" : m.vat_number || draft.vat_number,
        routing_id: m.routing_id || m.siren || draft.routing_id,
        vat_valid: micro ? false : Boolean(m.vat_valid) || draft.vat_valid,
        tax_exempt: micro,
        address_line: m.address_line || draft.address_line,
        address_line2: m.address_line2 || draft.address_line2,
        postal_code: m.postal_code || draft.postal_code,
        city: m.city || draft.city,
        region: m.region || draft.region,
        country_code: m.country_code || draft.country_code,
        sirene_status: m.sirene_status,
        sirene_closed_on: m.sirene_closed_on || "",
        sirene_created_on: m.sirene_created_on || "",
      };
      setDraft(next);
      setFoundName(next.name);
      setPhase("ok");
      setFlash((n) => n + 1);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Recherche SIRET impossible";
      setPhase("err");
      setLookupErr(msg);
      onError(msg);
    } finally {
      setBusy(false);
    }
  }

  const kind = clientKindOf(draft);
  const particular = kind === "particulier";
  const micro = kind === "micro";
  const siretOk = draft.siret.replace(/\D/g, "").length === 14;
  const sirenOk = draft.siren.replace(/\D/g, "").length === 9;
  const siretMiss = Boolean(tried && ((kind === "societe" && !siretOk) || (micro && !siretOk && !sirenOk)));
  const sirenMiss = Boolean(tried && ((kind === "societe" && !sirenOk) || (micro && !siretOk && !sirenOk)));
  const vatMiss = Boolean(tried && kind === "societe" && !draft.vat_number.trim());
  const nameMiss = Boolean(tried && !draft.name.trim());

  return (
    <section className="flex flex-col gap-3">
      <h2 className="m-0 font-[Nunito] text-lg">Informations sur l’entreprise</h2>
      <div className="fx-kind-picks" role="radiogroup" aria-label="Type de client">
        {KINDS.map((k) => (
          <button
            key={k.id}
            type="button"
            role="radio"
            aria-checked={kind === k.id}
            className={kind === k.id ? "is-on" : ""}
            disabled={busy}
            onClick={() => setDraft(applyClientKind(draft, k.id))}
          >
            <strong>{k.label}</strong>
            <em>{k.hint}</em>
          </button>
        ))}
      </div>
      <p className="m-0 text-xs" style={{ color: "var(--fx-muted)" }}>
        {micro
          ? "Micro : SIRET ou SIREN + adresse. La TVA est grisée."
          : particular
            ? "Particulier : nom et adresse. Identifiants grisés."
            : "Société : SIRET, SIREN, TVA et adresse — obligatoires dès l’enregistrement."}
      </p>
      <SiretSearch
        value={draft.siret}
        onChange={(siret) => {
          const prev = draft.siret.replace(/\D/g, "");
          const next = siret.replace(/\D/g, "");
          setDraft({
            ...draft,
            siret,
            ...(next !== prev ? { sirene_status: undefined, sirene_closed_on: undefined, sirene_created_on: undefined } : {}),
          });
          if (phase !== "scan") setPhase("idle");
        }}
        phase={phase}
        foundName={foundName}
        error={lookupErr}
        disabled={busy || particular}
        activity={
          draft.sirene_status
            ? {
                sirene_status: draft.sirene_status,
                sirene_closed_on: draft.sirene_closed_on,
                sirene_created_on: draft.sirene_created_on,
              }
            : null
        }
        onSearch={() => void lookup()}
        required={!particular}
        showMiss={siretMiss}
        missText={micro ? "SIRET (14) ou SIREN (9) pour enregistrer." : "SIRET à 14 chiffres pour enregistrer."}
      />
      <div key={flash} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className={`sm:col-span-2${flash ? " fx-reveal" : ""}`} style={flash ? { animationDelay: "0ms" } : undefined}>
          <Field
            label="Nom"
            name="name"
            required
            showMiss={nameMiss}
            missText="Le nom est obligatoire pour enregistrer."
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
        </div>
        <div className={`sm:col-span-2${flash ? " fx-reveal" : ""}`} style={flash ? { animationDelay: "40ms" } : undefined}>
          <Field
            label="Nom d’affichage"
            name="display_name"
            value={draft.display_name}
            onChange={(e) => setDraft({ ...draft, display_name: e.target.value })}
          />
        </div>
        <div className={flash ? "fx-reveal" : ""} style={flash ? { animationDelay: "80ms" } : undefined}>
          <Field
            label="Numéro de TVA"
            name="vat_number"
            required={kind === "societe"}
            showMiss={vatMiss}
            missText="Le n° de TVA est obligatoire pour une société."
            disabled={particular || micro}
            value={draft.vat_number}
            hint={micro ? "Grisé — micro / franchise" : particular ? "Grisé — particulier" : undefined}
            onChange={(e) => setDraft({ ...draft, vat_number: e.target.value })}
          />
        </div>
        <div className={flash ? "fx-reveal" : ""} style={flash ? { animationDelay: "160ms" } : undefined}>
          <Field
            label="SIREN"
            name="siren"
            required={!particular}
            showMiss={sirenMiss}
            missText={micro ? "SIRET (14) ou SIREN (9) pour enregistrer." : "SIREN à 9 chiffres pour enregistrer."}
            disabled={particular}
            hint="9 chiffres — mention obligatoire client, champ distinct du SIRET"
            value={draft.siren}
            inputMode="numeric"
            onChange={(e) => setDraft({ ...draft, siren: e.target.value.replace(/\D/g, "").slice(0, 9) })}
          />
        </div>
        {kind === "societe" ? (
          <div className={`sm:col-span-2 flex flex-wrap gap-5${flash ? " fx-reveal" : ""}`} style={flash ? { animationDelay: "200ms" } : undefined}>
            <CheckField label="Numéro de TVA valide" checked={draft.vat_valid} onChange={(v) => setDraft({ ...draft, vat_valid: v })} />
          </div>
        ) : null}
        <div className={flash ? "fx-reveal" : ""} style={flash ? { animationDelay: "240ms" } : undefined}>
          <Field
            label="Site web"
            name="website"
            value={draft.website}
            onChange={(e) => setDraft({ ...draft, website: e.target.value })}
          />
        </div>
        <div className={flash ? "fx-reveal" : ""} style={flash ? { animationDelay: "320ms" } : undefined}>
          <Field
            label="Téléphone"
            name="phone"
            value={draft.phone}
            onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
          />
        </div>
      </div>
      <PcgFields
        accounts={PCG_CUSTOMERS}
        code={draft.pcg_code}
        sub={draft.pcg_sub}
        onCode={(v) => setDraft({ ...draft, pcg_code: v })}
        onSub={(v) => setDraft({ ...draft, pcg_sub: v })}
        codeLabel="Compte client"
      />
    </section>
  );
}
